import { invalidateBidSaves } from '../api/bid-save-queue';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { restoreSession } from './session-restore';
import type { Session } from '../api/types';

/**
 * Кто вошёл.
 *
 * СТРАНИЦА НЕ МОЖЕТ ЭТОГО ЗНАТЬ, ОНА МОЖЕТ ТОЛЬКО СПРОСИТЬ. Ключи лежат
 * в печеньях httpOnly и скрипту не видны — это и есть смысл httpOnly.
 * При загрузке кабинет читает действующую сессию с сервера. Если доступ
 * истёк, общий API-клиент один раз обновляет его и повторяет чтение.
 *
 * ХРАНИТЬ ПРИЗНАК ВХОДА В localStorage НЕЛЬЗЯ. Он переживёт и выход
 * с другой вкладки, и завершение сеанса администратором, и смену пароля —
 * человек будет видеть кабинет, в котором ни один запрос не работает.
 * Единственная правда о входе живёт на сервере.
 */

/**
 * Чем кончился ввод пароля.
 *
 * РАЗЛИЧАЕТСЯ ЯВНО, А НЕ «СЕССИЯ ЛИБО NULL». Страница входа обязана
 * разобрать оба случая: молчаливое приведение к сессии однажды пустило
 * бы человека мимо второго рубежа — то есть отменило бы ровно ту
 * защиту, которую он включил.
 */
export type SignIn =
  | { kind: 'in' }
  | {
      kind: 'totp';
      /** Промежуточный ключ. Сам по себе не даёт ничего, живёт 5 минут. */
      challenge: string;
    };

interface SessionState {
  session: Session | null;
  /** Первый ответ ещё не получен: показывать вход рано, кабинет — тоже. */
  loading: boolean;
  /** A transport/service failure does not prove that the user signed out. */
  restoreFailed: boolean;
  signIn(email: string, password: string): Promise<SignIn>;
  /** Второй шаг: код из приложения либо запасной код. */
  signInWithTotp(challenge: string, code: string, recovery?: boolean): Promise<void>;
  signOut(): Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const [signedIn, setSignedIn] = useState<Session | null>(null);

  const restored = useQuery({
    queryKey: ['session'],
    queryFn: restoreSession,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignIn> => {
      const answer = await api<Session | { totp: true; challenge: string }>('/auth/login', {
        method: 'POST',
        body: { email, password },
      });

      /**
       * ПАРОЛЬ СОШЁЛСЯ, НО ЧЕЛОВЕК ЕЩЁ НЕ ВОШЁЛ. Печений сессии сервер
       * здесь не ставит, и записывать сюда что-либо как в вошедшего
       * нельзя: кабинет открылся бы мимо второго рубежа.
       */
      if ('totp' in answer) return { kind: 'totp', challenge: answer.challenge };

      invalidateBidSaves();
      setSignedIn(answer);
      client.setQueryData(['session'], answer);
      return { kind: 'in' };
    },
    [client],
  );

  const signInWithTotp = useCallback(
    async (challenge: string, code: string, recovery = false) => {
      const s = await api<Session>('/auth/login/totp', {
        method: 'POST',
        body: { challenge, code, recovery },
      });
      invalidateBidSaves();
      setSignedIn(s);
      client.setQueryData(['session'], s);
    },
    [client],
  );

  const signOut = useCallback(async () => {
    invalidateBidSaves();
    try {
      await api('/auth/logout', { method: 'POST', body: {} });
    } finally {
      // Чистим кеш при выходе безусловно, даже если запрос не дошёл.
      // Данные чужого кабинета, оставшиеся на экране после выхода, —
      // это ровно то, чего требует не допускать НФ-5.
      setSignedIn(null);
      client.setQueryData(['session'], null);
      client.clear();
    }
  }, [client]);

  const value = useMemo<SessionState>(
    () => ({
      session: signedIn ?? restored.data ?? null,
      loading: restored.isLoading,
      restoreFailed: restored.isError,
      signIn,
      signInWithTotp,
      signOut,
    }),
    [signedIn, restored.data, restored.isLoading, restored.isError, signIn, signInWithTotp, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession вызван вне SessionProvider');
  return v;
}
